import {
  DefaultButton,
  IconButton,
  PrimaryButton,
  Stack,
  TextField,
  Toggle,
} from '@fluentui/react';
import { useSetAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import {
  isInProgressAtom,
  errorMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  createWorkImage,
  deleteWorkImage,
  getWorkImage,
  getWorkImages,
  IWorkImageContent,
} from '../../services/workService';
import { useBoolean } from '@fluentui/react-hooks';
import { ImagePreviewDialog, PhotoEditor } from '@moko/ui';

export interface IWorkPhotosParams {
  workId: number | undefined;
}
interface Photo {
  id: number;
  thumbnailContent: string;
  fullImageUrl: string;
  type?: string;
  caption?: string;
}
const WorkPhotos = (params: IWorkPhotosParams) => {
  const setIsInProgress = useSetAtom(isInProgressAtom);
  const [dialogContent, setDialogContent] = useState<IWorkImageContent | null>(
    null
  );
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const setErrorMessage = useSetAtom(errorMessageAtom);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [refreshCount, setRefreshCount] = useState(0);
  const [
    isUserOrGroupSelectorOpen,
    { setTrue: showUserOrGroupSelector, setFalse: hideUserOrGroupSelector },
  ] = useBoolean(false);

  const [mode, setMode] = useState<'photo' | 'video'>('photo');
  const [showCamera, setShowCamera] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [photoEditing, setPhotoEditing] = useState(false);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [location, setLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [recording, setRecording] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);

  useEffect(() => {
    const abortController = new AbortController();
    const fetchData = async () => {
      setIsInProgress(true);
      try {
        if (!params.workId) {
          throw new Error('workId not provided');
        }
        const data = await getWorkImages(abortController, params.workId);
        setPhotos(
          (data?.items ?? []).map((p) => ({
            id: p.id,
            thumbnailContent: p.imageThumbnail,
            fullImageUrl: p.imageUrl,
            type: p.type,
            caption: p.caption?.trim() || undefined,
          }))
        );
      } catch (error: any) {
        console.error('Error:', error);
        setErrorMessage(error.message);
      } finally {
        setIsInProgress(false);
      }
    };

    fetchData();
    return () => {
      abortController.abort();
    };
  }, [params.workId, refreshCount]);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  const handleViewImage = async (photoId: number) => {
    showUserOrGroupSelector();
    setDialogContent(null);
    setDialogLoading(true);
    setDialogError(null);
    try {
      const abortController = new AbortController();
      // Fetch image content from API
      const data = await getWorkImage(abortController, photoId);
      setDialogContent(data); // Should be a data URL or base64 string
    } catch (err: any) {
      setErrorMessage(err.message);
      setDialogError(err.message);
    } finally {
      setDialogLoading(false);
    }
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });
      setStream(mediaStream);
    } catch {
      setErrorMessage('Unable to access camera');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          setLocation({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          }),
        () => setLocation(null)
      );
    } else {
      setLocation(null);
    }
  };

  const handleOpenCamera = () => {
    setShowCamera(true);
    startCamera();
  };

  const handleTakePhoto = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      setPreview(canvas.toDataURL('image/jpeg'));
    }
    getLocation();
    stopCamera();
    setShowCamera(false);
  };

  const handleStartRecording = () => {
    if (!videoRef.current || !stream) return;
    recordedChunksRef.current = [];
    const mediaRecorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) recordedChunksRef.current.push(event.data);
    };
    mediaRecorder.onstop = () => {
      const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
      setVideoBlob(blob);
      setVideoPreview(URL.createObjectURL(blob));
      getLocation();
      stopCamera();
      setShowCamera(false);
      setRecording(false);
    };
    mediaRecorder.start();
    setRecording(true);
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
    }
  };

  const handleUploadPhoto = async () => {
    if (!preview || !params.workId) return;
    setIsInProgress(true);
    try {
      const abortController = new AbortController();
      const trimmedCaption = caption.trim();
      await createWorkImage(abortController, {
        workId: params.workId,
        imageContent: preview,
        type: 'photo',
        location,
        ...(trimmedCaption ? { caption: trimmedCaption } : {}),
      });
      setPreview(null);
      setCaption('');
      setLocation(null);
      setRefreshCount((prev) => prev + 1);
    } catch (err: any) {
      setErrorMessage(err.message);
    } finally {
      setIsInProgress(false);
    }
  };

  const handleUploadVideo = async () => {
    if (!videoBlob || !params.workId) return;
    setIsInProgress(true);
    try {
      const abortController = new AbortController();
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const trimmedCaption = caption.trim();
          await createWorkImage(abortController, {
            workId: params.workId as number,
            imageContent: reader.result as string,
            type: 'video',
            location,
            ...(trimmedCaption ? { caption: trimmedCaption } : {}),
          });
          setVideoPreview(null);
          setVideoBlob(null);
          setCaption('');
          setLocation(null);
          setRefreshCount((prev) => prev + 1);
        } catch (err: any) {
          setErrorMessage(err.message);
        } finally {
          setIsInProgress(false);
        }
      };
      reader.readAsDataURL(videoBlob);
    } catch (err: any) {
      setErrorMessage(err.message);
      setIsInProgress(false);
    }
  };

  const handleDeleteImage = async (photoId: number) => {
    setIsInProgress(true);
    try {
      const abortController = new AbortController();
      await deleteWorkImage(abortController, photoId);
      setRefreshCount((prev) => prev + 1);
    } catch (err: any) {
      setErrorMessage(err.message);
    } finally {
      setIsInProgress(false);
    }
  };

  const captionsSrc = '/path/to/your/captions.vtt';

  return (
    <>
      {!showCamera && !preview && !videoPreview && (
        <Stack tokens={{ childrenGap: 12 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {photos.map((photo) => (
              <div
                key={photo.id}
                style={{
                  width: 120,
                  border: '1px solid #eee',
                  borderRadius: 8,
                  padding: 8,
                  margin: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <img
                  src={photo.thumbnailContent}
                  alt={photo.caption || 'photo'}
                  width={100}
                  height={100}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                />
                {photo.caption ? (
                  <div
                    title={photo.caption}
                    style={{
                      width: '100%',
                      marginTop: 4,
                      textAlign: 'center',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {photo.caption}
                  </div>
                ) : null}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    marginTop: 8,
                  }}
                >
                  <IconButton
                    iconProps={{
                      iconName: photo.type === 'video' ? 'Play' : 'ZoomIn',
                    }}
                    title="View"
                    ariaLabel="View"
                    onClick={() => handleViewImage(photo.id)}
                  />
                  <IconButton
                    iconProps={{ iconName: 'Delete' }}
                    title="Delete"
                    ariaLabel="Delete"
                    onClick={() => handleDeleteImage(photo.id)}
                  />
                </div>
              </div>
            ))}
          </div>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Toggle
              label="Video"
              inlineLabel
              checked={mode === 'video'}
              onChange={(_, checked) => setMode(checked ? 'video' : 'photo')}
            />
            <PrimaryButton
              iconProps={{ iconName: mode === 'photo' ? 'Camera' : 'Video' }}
              onClick={handleOpenCamera}
            >
              {mode === 'photo' ? 'Take Photo' : 'Record Video'}
            </PrimaryButton>
          </Stack>
        </Stack>
      )}

      {showCamera && (
        <Stack tokens={{ childrenGap: 8 }}>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{ width: 320, height: 240 }}
          />
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            {mode === 'photo' ? (
              <PrimaryButton onClick={handleTakePhoto}>Capture</PrimaryButton>
            ) : !recording ? (
              <PrimaryButton onClick={handleStartRecording}>
                Start Recording
              </PrimaryButton>
            ) : (
              <PrimaryButton onClick={handleStopRecording}>
                Stop Recording
              </PrimaryButton>
            )}
            <DefaultButton
              onClick={() => {
                stopCamera();
                setShowCamera(false);
                setRecording(false);
              }}
            >
              Cancel
            </DefaultButton>
          </Stack>
        </Stack>
      )}

      {preview && (
        <Stack tokens={{ childrenGap: 8 }}>
          <PhotoEditor
            src={preview}
            onSrcChange={setPreview}
            onEditingChange={setPhotoEditing}
          />
          <TextField
            label="Caption"
            value={caption}
            disabled={photoEditing}
            onChange={(_, value) => setCaption(value ?? '')}
          />
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <PrimaryButton onClick={handleUploadPhoto} disabled={photoEditing}>
              Upload
            </PrimaryButton>
            <DefaultButton
              onClick={() => {
                setPreview(null);
                setCaption('');
                setLocation(null);
              }}
            >
              Discard
            </DefaultButton>
          </Stack>
        </Stack>
      )}

      {videoPreview && (
        <Stack tokens={{ childrenGap: 8 }}>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            src={videoPreview}
            controls
            style={{ width: 320, height: 240 }}
          />
          <TextField
            label="Caption"
            value={caption}
            onChange={(_, value) => setCaption(value ?? '')}
          />
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <PrimaryButton onClick={handleUploadVideo}>Upload</PrimaryButton>
            <DefaultButton
              onClick={() => {
                setVideoPreview(null);
                setVideoBlob(null);
                setCaption('');
                setLocation(null);
              }}
            >
              Discard
            </DefaultButton>
          </Stack>
        </Stack>
      )}

      <ImagePreviewDialog
        open={isUserOrGroupSelectorOpen}
        onClose={hideUserOrGroupSelector}
        loading={dialogLoading}
        error={dialogError}
        content={dialogContent}
        captionsSrc={captionsSrc}
        labels={{
          title: 'Image Preview',
          close: 'Close',
          loading: 'Loading',
          location: 'Location',
          note: 'Note',
          showAnalytics: 'Show Analytics',
          hideAnalytics: 'Hide Analytics',
          objects: 'Objects',
          persons: 'Persons',
          texts: 'Texts',
        }}
      />
    </>
  );
};

export default WorkPhotos;
